<template>
  <div v-loading="loading" class="sai-container">
    <!-- Actions Bar -->
    <div class="sai-actions">
      <el-button
        type="primary"
        icon="el-icon-plus"
        :disabled="!loaded || loading || busy"
        class="sai-btn sai-btn--primary"
        @click="openDialog()"
      >
        Thêm IP
      </el-button>
      <el-button
        icon="el-icon-refresh"
        :disabled="loading || busy"
        class="sai-btn"
        @click="loadIps"
      >
        Tải lại
      </el-button>
    </div>

    <!-- Notes & Alerts (theo quy chuẩn sk-note) -->
    <div class="sk-note mb-3">
      <i class="el-icon-info" />
      <span>Quản lý danh sách IP SePay được chấp nhận. Hỗ trợ địa chỉ IPv4 và IPv6.</span>
    </div>

    <div v-if="error" class="sk-note sk-note--danger mb-3">
      <i class="el-icon-error" />
      <span>{{ error }}</span>
    </div>

    <div v-if="loaded && !ips.length" class="sk-note sk-note--warning mb-3">
      <i class="el-icon-warning-outline" />
      <span>Chưa có IP nào trong danh sách được chấp nhận.</span>
    </div>

    <!-- Table Card Wrapper (theo quy chuẩn sk-table) -->
    <div v-if="loaded" class="sai-table-wrapper">
      <el-table
        :data="ips"
        :border="true"
        class="sk-table"
        style="width: 100%"
        empty-text="Chưa có IP được chấp nhận"
      >
        <!-- Cột STT: chuẩn type="index", width 50, align="center" -->
        <el-table-column type="index" label="STT" width="50" align="center" />

        <!-- Cột Địa chỉ IP -->
        <el-table-column prop="ipAddress" label="Địa chỉ IP" min-width="220">
          <template slot-scope="{ row }">
            <span class="sai-ip-text">{{ row.ipAddress }}</span>
          </template>
        </el-table-column>

        <!-- Cột Thao tác: chuẩn icon-circle button kết hợp el-tooltip -->
        <el-table-column label="Thao tác" width="120" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-tooltip content="Chỉnh sửa" placement="top">
              <el-button
                type="primary"
                icon="el-icon-edit"
                size="mini"
                circle
                plain
                :disabled="busy || loading"
                @click="openDialog(row)"
              />
            </el-tooltip>
            <el-tooltip content="Xóa" placement="top">
              <el-button
                type="danger"
                icon="el-icon-delete"
                size="mini"
                circle
                plain
                class="ml-1"
                :disabled="busy || loading"
                @click="deleteIp(row)"
              />
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Dialog thêm / sửa IP dùng DialogBase theo quy chuẩn -->
    <DialogBase
      :title="editingId ? 'Sửa IP được chấp nhận' : 'Thêm IP được chấp nhận'"
      :visible.sync="dialogVisible"
      dialog-class="sk-dialog--sm"
      :use-panel="false"
      :append-to-body="true"
      :close-on-click-modal="false"
      @close="handleCloseDialog"
    >
      <div class="sk-dialog__content">
        <div class="sk-panel">
          <div class="sk-panel-head">
            <div class="sk-panel-icon">
              <i class="el-icon-connection" />
            </div>
            <div>
              <p class="sk-panel-title">
                {{ editingId ? 'Cập nhật địa chỉ IP' : 'Thêm mới địa chỉ IP' }}
              </p>
              <p class="sk-panel-subtitle">Hỗ trợ địa chỉ IPv4 hoặc IPv6 của SePay</p>
            </div>
          </div>

          <el-form
            ref="ipForm"
            :model="form"
            :rules="rules"
            label-position="top"
            @submit.native.prevent="saveIp"
          >
            <el-form-item label="Địa chỉ IPv4 hoặc IPv6" prop="ipAddress">
              <el-input
                v-model.trim="form.ipAddress"
                maxlength="45"
                placeholder="Ví dụ: 103.255.238.9"
                :disabled="saving"
                clearable
                @keyup.enter.native="saveIp"
              />
            </el-form-item>
          </el-form>
        </div>
      </div>

      <template #footer>
        <div class="sk-dialog__footer">
          <el-button class="btn-cancel" plain :disabled="saving" @click="dialogVisible = false">
            Hủy
          </el-button>
          <el-button class="btn-save" type="primary" :loading="saving" @click="saveIp">
            Lưu
          </el-button>
        </div>
      </template>
    </DialogBase>
  </div>
</template>

<script>
import SePayTokenManagementService from "@/services/SePayTokenManagementService";
import DialogBase from "@/components/DialogBase.vue";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: "SePayAllowedIpManagement",
  components: { DialogBase },
  data() {
    return {
      ips: [],
      loaded: false,
      loading: false,
      saving: false,
      deleting: false,
      error: "",
      dialogVisible: false,
      editingId: null,
      form: { ipAddress: "" },
      rules: {
        ipAddress: [
          { required: true, message: "Vui lòng nhập địa chỉ IP", trigger: "blur" }
        ]
      }
    };
  },
  computed: {
    busy() {
      return this.saving || this.deleting;
    }
  },
  mounted() {
    this.loadIps();
  },
  methods: {
    errorMessage(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    async loadIps() {
      this.loading = true;
      this.error = "";
      try {
        const response = await SePayTokenManagementService.getAllowedIps();
        this.ips = response.data.data || [];
        this.loaded = true;
      } catch (error) {
        this.loaded = false;
        this.error = this.errorMessage(error, "Không thể tải danh sách IP. Vui lòng thử lại.");
        NotifyMessage.leftCornerNotificationError(this.error);
      } finally {
        this.loading = false;
      }
    },
    openDialog(row) {
      this.editingId = row ? row.id : null;
      this.form = { ipAddress: row ? row.ipAddress : "" };
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.ipForm && this.$refs.ipForm.clearValidate();
      });
    },
    handleCloseDialog() {
      this.$refs.ipForm && this.$refs.ipForm.clearValidate();
    },
    async saveIp() {
      if (this.saving) return;
      try {
        await this.$refs.ipForm.validate();
      } catch (_) {
        return;
      }
      this.saving = true;
      try {
        const payload = { ipAddress: this.form.ipAddress.trim() };
        if (this.editingId) {
          await SePayTokenManagementService.updateIp(this.editingId, payload);
        } else {
          await SePayTokenManagementService.createIp(payload);
        }
        NotifyMessage.leftCornerNotificationSuccess("Lưu địa chỉ IP thành công");
        this.dialogVisible = false;
        await this.loadIps();
      } catch (error) {
        NotifyMessage.leftCornerNotificationError(
          this.errorMessage(error, "Không thể lưu địa chỉ IP. Vui lòng thử lại.")
        );
      } finally {
        this.saving = false;
      }
    },
    async deleteIp(row) {
      if (this.busy) return;
      const warning = this.ips.length === 1 ? " Đây là IP cuối cùng trong danh sách." : "";
      try {
        await this.$confirm(
          `Bạn có chắc chắn muốn xóa IP ${row.ipAddress}?${warning} Thao tác không thể hoàn tác.`,
          "Xác nhận xóa",
          {
            confirmButtonText: "Xóa",
            cancelButtonText: "Hủy",
            type: "warning"
          }
        );
      } catch (_) {
        return;
      }
      this.deleting = true;
      try {
        await SePayTokenManagementService.deleteIp(row.id);
        NotifyMessage.leftCornerNotificationSuccess("Xóa địa chỉ IP thành công");
        await this.loadIps();
      } catch (error) {
        NotifyMessage.leftCornerNotificationError(
          this.errorMessage(error, "Không thể xóa địa chỉ IP. Vui lòng thử lại.")
        );
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";
@import "@/assets/style/sk-table.scss";

.sai-container {
  padding: 4px 0 12px;
}

.sai-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.sai-btn {
  border-radius: 10px;
  font-weight: 600;
}

.sai-table-wrapper {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #dbeafe;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.sai-ip-text {
  font-weight: 600;
  color: #1e293b;
}

/* Header bảng chuẩn màu xanh primary theo guidelines.md mục 10.4 */
/deep/ .sk-table thead th,
/deep/ .sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/deep/ .sk-table thead th > .cell,
/deep/ .sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
}

/deep/ .sk-table thead tr {
  height: 50px;
}

/deep/ .sk-table tbody td {
  background: #ffffff;
}

/deep/ .el-input__inner {
  border-radius: 10px;
}
</style>
